
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../integrations/supabase/client';
import { Users, Briefcase, Car, Calendar, Star, AlertCircle } from 'lucide-react';
import type { Tables } from '../integrations/supabase/types';
import { Link } from 'react-router-dom';

const FleetPage = () => {
  const { data: vehicles, isLoading, error } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      console.log('Fetching vehicles data...');
      const { data, error } = await supabase
        .from('vehicles')
        .select('*');
      
      if (error) {
        console.error('Error fetching vehicles:', error);
        throw error;
      }
      
      console.log('Vehicles data received:', data);
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    console.error('Fleet page error:', error);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Unable to Load Fleet Data</h2>
        <p className="text-gray-600 mb-6 text-center max-w-md">
          We encountered an issue while loading our fleet information. Please try again later.
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          Refresh
        </button>
      </div>
    );
  }

  const getVehicleIcon = (type: string) => {
    switch (type) {
      case 'luxury':
        return <Star className="w-6 h-6 text-amber-500" />;
      case 'van':
        return <Users className="w-6 h-6 text-blue-500" />;
      default:
        return <Car className="w-6 h-6 text-primary" />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-900 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-6">Our Fleet</h1>
          <p className="text-xl text-gray-300">Discover our range of luxury vehicles</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {(!vehicles || vehicles.length === 0) ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">No Vehicles Available</h2>
            <p className="text-gray-600">Our fleet information is currently being updated. Please check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vehicles?.map((vehicle: Tables<'vehicles'>) => (
              <div key={vehicle.id} className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 hover:shadow-xl">
                <div className="relative">
                  <img src={vehicle.image_url || '/placeholder.svg'} alt={vehicle.name} className="w-full h-64 object-cover" />
                  <div className="absolute top-4 right-4 bg-white/90 rounded-full p-2">
                    {getVehicleIcon(vehicle.type)}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold mb-4">{vehicle.name}</h3>
                  <p className="text-gray-600 mb-4">{vehicle.description}</p>
                  
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex items-center gap-4 text-gray-600">
                      <div className="flex items-center">
                        <Users className="w-5 h-5 mr-2" />
                        <span>{vehicle.capacity} passengers</span>
                      </div>
                      <div className="flex items-center">
                        <Briefcase className="w-5 h-5 mr-2" />
                        <span>{vehicle.luggage_capacity} luggage</span>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-5 h-5 mr-2" />
                      <span>Available 24/7</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-primary">€{vehicle.price_per_km}/km</span>
                    <Link
                      to="/reservation"
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-center mb-8">Why Choose Our Fleet?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="bg-primary/10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <Star className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Premium Vehicles</h3>
              <p className="text-gray-600">All our vehicles are late models and meticulously maintained</p>
            </div>
            <div className="text-center p-4">
              <div className="bg-primary/10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Professional Drivers</h3>
              <p className="text-gray-600">Experienced, licensed, and professional chauffeurs</p>
            </div>
            <div className="text-center p-4">
              <div className="bg-primary/10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">24/7 Availability</h3>
              <p className="text-gray-600">Service available any time of day or night</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetPage;
